// Surveys module (Surveys Task 1 — Foundation).
//
// Multi-page, branching survey schema. A SurveyVersion snapshots an ordered
// array of pages; each page holds ordered fields + branching rules. These
// types are the single source of truth shared by the API (Zod boundary in
// modules/surveys/surveys.schemas.ts) and the web builder/renderer.

export type SurveyStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type SurveyAudienceMode = 'ANONYMOUS' | 'IDENTIFIED';
export type SurveyResponseLimit = 'MANY' | 'ONE';
export type SurveyDedupeKey = 'NONE' | 'EMAIL' | 'PHONE' | 'TOKEN';
export type SurveyResponseStatus = 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';

export type SurveyFieldType =
  | 'SHORT_TEXT'
  | 'LONG_TEXT'
  | 'EMAIL'
  | 'PHONE'
  | 'NUMBER'
  | 'DATE'
  | 'SINGLE_SELECT'
  | 'MULTI_SELECT'
  | 'NUMERIC_SCALE'
  | 'LIKERT'
  | 'NPS'
  | 'CONSENT_CHECKBOX';

/**
 * Canonical contact-key tag. When a field is tagged, the Task 6 contact-sync
 * worker reads its answer to create a new contact (never to update one). A
 * survey with no tagged fields simply collects data without creating contacts.
 */
export type SurveyContactKey = 'email' | 'first_name' | 'last_name' | 'company';

export interface SurveySelectOption {
  label: string;
  value: string;
}

export interface SurveyLikertOption {
  label: string;
  /** Implicit numeric rank (1..n) used for aggregation. */
  value: number;
}

interface SurveyFieldBase {
  key: string;
  label: string;
  help_text?: string;
  /** The mandatory flag — gates page advance when the field is presented. */
  required: boolean;
  contact_key?: SurveyContactKey;
}

export type SurveyFieldDefinition =
  | (SurveyFieldBase & { type: 'SHORT_TEXT'; max_length?: number })
  | (SurveyFieldBase & { type: 'LONG_TEXT'; max_length?: number })
  | (SurveyFieldBase & { type: 'EMAIL' })
  | (SurveyFieldBase & { type: 'PHONE' })
  | (SurveyFieldBase & { type: 'NUMBER'; min?: number; max?: number; step?: number })
  | (SurveyFieldBase & { type: 'DATE' })
  | (SurveyFieldBase & { type: 'SINGLE_SELECT'; options: SurveySelectOption[] })
  | (SurveyFieldBase & { type: 'MULTI_SELECT'; options: SurveySelectOption[] })
  | (SurveyFieldBase & {
      type: 'NUMERIC_SCALE';
      min: number;
      max: number;
      step?: number;
      min_label?: string;
      max_label?: string;
    })
  | (SurveyFieldBase & { type: 'LIKERT'; options: SurveyLikertOption[] })
  | (SurveyFieldBase & { type: 'NPS' })
  | (SurveyFieldBase & { type: 'CONSENT_CHECKBOX'; text_override?: string });

export type SurveyBranchOperator = 'equals' | 'not_equals' | 'contains' | 'gte' | 'lte';

export interface SurveyBranchCondition {
  /** Must reference a field on a PRIOR page (forward-only). */
  field_key: string;
  operator: SurveyBranchOperator;
  value: string | number;
}

/** Sentinel goto target meaning "finish the survey". */
export const SURVEY_END_PAGE_ID = 'END';

export interface SurveyBranchRule {
  id: string;
  /** How `conditions` combine. */
  connector: 'AND' | 'OR';
  conditions: SurveyBranchCondition[];
  /** A page id, or SURVEY_END_PAGE_ID to finish. */
  goto_page_id: string;
}

export interface SurveyPageDefinition {
  id: string;
  label: string;
  fields: SurveyFieldDefinition[];
  /** Evaluated in order; first match wins; fallback = next sequential page. */
  branching_rules: SurveyBranchRule[];
}

/** The JSON snapshot stored on SurveyVersion.pages. */
export type SurveyPages = SurveyPageDefinition[];

// ─── Flow validation (shared by builder + publish route) ───────────────────────

export interface SurveyValidationIssue {
  code: string;
  message: string;
  page_id?: string;
  field_key?: string;
  rule_id?: string;
}

export interface SurveyFlowValidationResult {
  ok: boolean;
  errors: SurveyValidationIssue[];
  warnings: SurveyValidationIssue[];
}
